import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { DomainGroupPicker, PolicySchedulePicker } from "../../components/network/PolicyAssociationPickers";
import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { FormField } from "../../components/ui/FormField";
import { Glossary } from "../../components/ui/Glossary";
import { Input } from "../../components/ui/Input";
import { PageHeader } from "../../components/ui/PageHeader";
import { RadioCardGroup } from "../../components/ui/RadioCard";
import { ErrorState, LoadingState } from "../../components/ui/State";
import { Switch } from "../../components/ui/Switch";
import { ApiError } from "../../lib/api";
import { deviceGroupsApi } from "../../lib/api/network/deviceGroups";
import {
  type GroupPolicyDefaultAction,
  type PolicyActivation,
  type UnknownDomainAction,
  groupPoliciesApi,
} from "../../lib/api/network/groupPolicies";
import { urlGroupsApi } from "../../lib/api/network/urlGroups";
import { schedulesApi } from "../../lib/api/policies";
import { formatMessage, t } from "../../lib/i18n";
import { useRecompileNotice } from "../../lib/recompileNotice";
import { useLocale } from "../../lib/useLocale";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

type StepKey =
  | "mode"
  | "destinations"
  | "who"
  | "activation"
  | "details"
  | "review";
const STEPS: StepKey[] = [
  "mode",
  "destinations",
  "who",
  "activation",
  "details",
  "review",
];

export function RuleWizardPage() {
  useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const notice = useRecompileNotice();
  const [searchParams] = useSearchParams();

  // "Promote to permanent rule" from an approved access request lands here
  // with `?destination=value1, value2` (NetworkAccessRequestsPage.tsx
  // promoteToRule()). The wizard's destinations step only picks existing
  // URL Groups (no raw-value entry), so we can't auto-select a group — we
  // surface the pending values so the admin doesn't have to reopen the
  // access request to retype them (docs/admin/ux-audit.md P1-3).
  const prefillDestinations = useMemo(() => {
    const raw = searchParams.get("destination");
    if (!raw) return [];
    return raw
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }, [searchParams]);

  const [step, setStep] = useState<StepKey>("mode");
  const [defaultAction, setDefaultAction] =
    useState<GroupPolicyDefaultAction>("block");
  const [urlGroupIds, setUrlGroupIds] = useState<string[]>(() => searchParams.get("destinationGroup") ? [searchParams.get("destinationGroup")!] : []);
  const [deviceGroup, setDeviceGroup] = useState<string>("");
  const [activation, setActivation] = useState<PolicyActivation>("always");
  const [scheduleProfileId, setScheduleProfileId] = useState<string>("");
  const [unknownDomainAction, setUnknownDomainAction] =
    useState<UnknownDomainAction>("log");
  const [name, setName] = useState("");
  const [priority, setPriority] = useState(50);
  const [isActive, setIsActive] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [activeNotificationTitle, setActiveNotificationTitle] = useState("");
  const [activeNotificationBody, setActiveNotificationBody] = useState("");
  const [inactiveNotificationTitle, setInactiveNotificationTitle] = useState("");
  const [inactiveNotificationBody, setInactiveNotificationBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  const urlGroups = useQuery({
    queryKey: ["rules", "url-groups", "picker"],
    queryFn: urlGroupsApi.list,
  });
  const deviceGroups = useQuery({
    queryKey: ["wizard", "device-groups"],
    queryFn: deviceGroupsApi.list,
  });
  const existingRules = useQuery({
    queryKey: ["wizard", "rules"],
    queryFn: groupPoliciesApi.list,
  });
  const schedules = useQuery({
    queryKey: ["rules", "schedules", "picker"],
    queryFn: () => schedulesApi.list(),
  });

  const submitMut = useMutation({
    mutationFn: () =>
      groupPoliciesApi.create({
        name,
        device_group: deviceGroup,
        url_groups: urlGroupIds,
        default_action: defaultAction,
        activation,
        unknown_domain_action: unknownDomainAction,
        priority,
        is_active: isActive,
        schedule_profile_id:
          activation === "always" ? null : scheduleProfileId || null,
        notifications_enabled: notificationsEnabled,
        active_notification_title: activeNotificationTitle,
        active_notification_body: activeNotificationBody,
        inactive_notification_title: inactiveNotificationTitle,
        inactive_notification_body: inactiveNotificationBody,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules", "list"] });
      notice();
      navigate("/policies/rules");
    },
    onError: (err) => setError(translateError(err)),
  });

  const stepIdx = STEPS.indexOf(step);
  function go(direction: 1 | -1) {
    setError(null);
    const next = STEPS[stepIdx + direction];
    if (next) setStep(next);
  }

  function canProceed(): boolean {
    if (step === "mode") return true;
    if (step === "destinations") return urlGroupIds.length > 0;
    if (step === "who") return Boolean(deviceGroup);
    if (step === "activation") {
      if (activation === "schedule") return Boolean(scheduleProfileId);
      return true;
    }
    if (step === "details") return Boolean(name.trim());
    return true;
  }

  function attemptNext() {
    if (step === "destinations" && urlGroupIds.length === 0) {
      setError(t("ruleWizard.destinations.errorRequired"));
      return;
    }
    if (step === "who" && !deviceGroup) {
      setError(t("ruleWizard.who.errorRequired"));
      return;
    }
    if (step === "activation" && activation === "schedule" && !scheduleProfileId) {
      setError(t("ruleWizard.activation.errorScheduleRequired"));
      return;
    }
    if (step === "details" && !name.trim()) {
      setError(t("rules.editor.errors.nameRequired"));
      return;
    }
    go(1);
  }

  const selectedDeviceGroup = useMemo(
    () => deviceGroups.data?.find((g) => g.id === deviceGroup),
    [deviceGroups.data, deviceGroup],
  );
  const selectedUrlGroups = useMemo(
    () => urlGroups.data?.filter((g) => urlGroupIds.includes(g.id)) ?? [],
    [urlGroups.data, urlGroupIds],
  );
  const selectedSchedule = useMemo(
    () => schedules.data?.find((s) => s.id === scheduleProfileId),
    [schedules.data, scheduleProfileId],
  );
  const conflictingRules = useMemo(() => {
    if (!existingRules.data || !deviceGroup) return [];
    return existingRules.data.filter((r) => r.device_group === deviceGroup);
  }, [existingRules.data, deviceGroup]);
  const minPriority = useMemo(() => {
    if (!existingRules.data || existingRules.data.length === 0) return 100;
    return Math.min(...existingRules.data.map((r) => r.priority));
  }, [existingRules.data]);

  if (urlGroups.isLoading || deviceGroups.isLoading) return <LoadingState />;
  if (urlGroups.error)
    return (
      <ErrorState
        titleKey="states.errorTitle"
        message={translateError(urlGroups.error)}
      />
    );
  if (deviceGroups.error)
    return (
      <ErrorState
        titleKey="states.errorTitle"
        message={translateError(deviceGroups.error)}
      />
    );

  return (
    <div>
      <PageHeader
        eyebrow={t("ruleWizard.eyebrow")}
        title={
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {t("ruleWizard.title")}
            <Glossary termKey="rule" />
          </span>
        }
        subtitle={t("ruleWizard.subtitle")}
        actions={
          <Link to="/policies/rules">
            <Button variant="ghost">{t("common.cancel")}</Button>
          </Link>
        }
      />

      <Alert tone="info" title={t("ruleWizard.infoTitle")}>
        {t("ruleWizard.infoBody")}
      </Alert>

      <ol className="wizard__steps" style={{ marginTop: 16 }}>
        {STEPS.map((s, idx) => {
          const isActiveStep = s === step;
          const isDone = idx < stepIdx;
          return (
            <li
              key={s}
              className={`wizard__step${isActiveStep ? " wizard__step--active" : ""}${
                isDone ? " wizard__step--done" : ""
              }`}
            >
              <span className="wizard__step-num">{idx + 1}</span>
              {t(`ruleWizard.stepLabel.${s}`)}
            </li>
          );
        })}
      </ol>

      <Card>
        <div className="wizard__panel">
          {error && <Alert tone="danger">{error}</Alert>}

          {step === "mode" && (
            <>
              <h2 style={{ margin: 0, fontSize: 18 }}>
                {t("ruleWizard.mode.title")}
              </h2>
              <RadioCardGroup<GroupPolicyDefaultAction>
                name="wizard-mode"
                value={defaultAction}
                onChange={setDefaultAction}
                options={[
                  {
                    value: "allow",
                    title: t("ruleWizard.mode.blockListed.title"),
                    desc: t("ruleWizard.mode.blockListed.desc"),
                  },
                  {
                    value: "block",
                    title: t("ruleWizard.mode.allowListed.title"),
                    desc: t("ruleWizard.mode.allowListed.desc"),
                  },
                ]}
              />
            </>
          )}

          {step === "destinations" && (
            <>
              <h2 style={{ margin: 0, fontSize: 18 }}>
                {t("ruleWizard.destinations.title")}
              </h2>
              <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: 13 }}>
                {t("ruleWizard.destinations.subtitle")}
              </p>
              {prefillDestinations.length > 0 && (
                <Alert tone="info" title={t("ruleWizard.destinations.prefillTitle")}>
                  {formatMessage("ruleWizard.destinations.prefillBody", {
                    values: prefillDestinations.join(", "),
                  })}
                </Alert>
              )}
              <DomainGroupPicker value={urlGroupIds} onChange={setUrlGroupIds} />
            </>
          )}

          {step === "who" && (
            <>
              <h2 style={{ margin: 0, fontSize: 18 }}>{t("ruleWizard.who.title")}</h2>
              <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: 13 }}>
                {t("ruleWizard.who.subtitle")}
              </p>
              <FormField label={t("rules.editor.field.who")} required>
                {(id) => (
                  <select
                    id={id}
                    className="input"
                    value={deviceGroup}
                    onChange={(e) => setDeviceGroup(e.target.value)}
                  >
                    <option value="">{t("rules.editor.placeholder.who")}</option>
                    {deviceGroups.data?.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.members_count})
                      </option>
                    ))}
                  </select>
                )}
              </FormField>
              {selectedDeviceGroup?.is_default && (
                <Alert tone="warning" title={t("ruleWizard.who.everyoneWarningTitle")}>
                  {t("ruleWizard.who.everyoneWarningBody")}
                </Alert>
              )}
              <Link to="/network/device-groups" style={{ fontSize: 13 }}>
                {t("ruleWizard.who.manageLink")}
              </Link>
            </>
          )}

          {step === "activation" && (
            <>
              <h2 style={{ margin: 0, fontSize: 18 }}>
                {t("ruleWizard.activation.title")}
              </h2>
              <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: 13 }}>
                {t("ruleWizard.activation.subtitle")}
              </p>
              <RadioCardGroup<PolicyActivation>
                name="wizard-activation"
                value={activation}
                onChange={(next) => {
                  setActivation(next);
                  if (next === "always") setScheduleProfileId("");
                }}
                options={[
                  {
                    value: "always",
                    title: t("ruleWizard.activation.always.title"),
                    desc: t("ruleWizard.activation.always.desc"),
                  },
                  {
                    value: "schedule",
                    title: t("ruleWizard.activation.schedule.title"),
                    desc: t("ruleWizard.activation.schedule.desc"),
                  },
                  {
                    value: "presence",
                    title: t("ruleWizard.activation.presence.title"),
                    desc: t("ruleWizard.activation.presence.desc"),
                  },
                ]}
              />
              {activation === "schedule" && (
                <>
                  <FormField
                    label={t("ruleWizard.activation.scheduleProfileLabel")}
                    required
                    hint={t("ruleWizard.activation.scheduleProfileHint")}
                  >
                    {(id) => (
                      <div id={id}><PolicySchedulePicker value={scheduleProfileId} onChange={setScheduleProfileId} /></div>
                    )}
                  </FormField>
                  {(schedules.data ?? []).length === 0 && (
                    <Alert tone="warning">
                      {t("ruleWizard.activation.noSchedules")}{" "}
                      <Link to="/policies/schedules">
                        {t("ruleWizard.activation.manageSchedulesLink")}
                      </Link>
                    </Alert>
                  )}
                </>
              )}
              {activation === "presence" && (
                <>
                  <Alert tone="info">{t("ruleWizard.activation.presenceInfo")}</Alert>
                  <FormField
                    label={t("ruleWizard.activation.presenceMaskLabel")}
                    hint={t("ruleWizard.activation.presenceMaskHint")}
                  >
                    {(id) => (
                      <select
                        id={id}
                        className="input"
                        value={scheduleProfileId}
                        onChange={(e) => setScheduleProfileId(e.target.value)}
                      >
                        <option value="">
                          {t("ruleWizard.activation.presenceMaskNone")}
                        </option>
                        {schedules.data?.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </FormField>
                </>
              )}
            </>
          )}

          {step === "details" && (
            <>
              <h2 style={{ margin: 0, fontSize: 18 }}>{t("ruleWizard.details.title")}</h2>
              <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: 13 }}>
                {t("ruleWizard.details.subtitle")}
              </p>
              <FormField label={t("ruleWizard.details.nameLabel")} required>
                {(id) => (
                  <Input
                    id={id}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("ruleWizard.details.namePlaceholder")}
                  />
                )}
              </FormField>
              <FormField
                label={t("ruleWizard.details.priorityLabel")}
                hint={t("ruleWizard.details.priorityHint")}
              >
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    value={priority}
                    onChange={(e) =>
                      setPriority(Number(e.target.value) || minPriority - 10)
                    }
                  />
                )}
              </FormField>
              {existingRules.data && existingRules.data.length > 0 && (
                <Alert tone="info">
                  {formatMessage("ruleWizard.details.priorityInfo", {
                    min: String(minPriority),
                  })}
                </Alert>
              )}
              <FormField label={t("ruleWizard.details.unknownDomainLabel")}>
                {(id) => (
                  <select
                    id={id}
                    className="input"
                    value={unknownDomainAction}
                    onChange={(e) =>
                      setUnknownDomainAction(e.target.value as UnknownDomainAction)
                    }
                  >
                    <option value="log">{t("unknownDomainAction.log")}</option>
                    <option value="block">{t("unknownDomainAction.block")}</option>
                    <option value="request_review">
                      {t("unknownDomainAction.request_review")}
                    </option>
                  </select>
                )}
              </FormField>
              <Switch
                checked={isActive}
                onChange={setIsActive}
                label={t("ruleWizard.details.activeLabel")}
              />
              <Switch
                checked={notificationsEnabled}
                onChange={setNotificationsEnabled}
                label={t("rules.notifications.enabled")}
              />
              {notificationsEnabled && (
                <>
                  <FormField label={t("rules.notifications.activeTitle")}>
                    {(id) => <Input id={id} value={activeNotificationTitle} onChange={(e) => setActiveNotificationTitle(e.target.value)} />}
                  </FormField>
                  <FormField label={t("rules.notifications.activeBody")}>
                    {(id) => <Input id={id} value={activeNotificationBody} onChange={(e) => setActiveNotificationBody(e.target.value)} />}
                  </FormField>
                  <FormField label={t("rules.notifications.inactiveTitle")}>
                    {(id) => <Input id={id} value={inactiveNotificationTitle} onChange={(e) => setInactiveNotificationTitle(e.target.value)} />}
                  </FormField>
                  <FormField label={t("rules.notifications.inactiveBody")} hint={t("rules.notifications.hint")}>
                    {(id) => <Input id={id} value={inactiveNotificationBody} onChange={(e) => setInactiveNotificationBody(e.target.value)} />}
                  </FormField>
                </>
              )}
            </>
          )}

          {step === "review" && (
            <>
              <h2 style={{ margin: 0, fontSize: 18 }}>{t("ruleWizard.review.title")}</h2>
              <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: 13 }}>
                {t("ruleWizard.review.subtitle")}
              </p>
              <div className="kv-grid">
                <div className="kv">
                  <div className="kv__label">{t("ruleWizard.review.name")}</div>
                  <div className="kv__value">{name || "—"}</div>
                </div>
                <div className="kv">
                  <div className="kv__label">{t("ruleWizard.review.mode")}</div>
                  <div className="kv__value">
                    {defaultAction === "allow" ? (
                      <span className="badge badge--danger">
                        {t("rules.action.blockListed")}
                      </span>
                    ) : (
                      <span className="badge badge--success">
                        {t("rules.action.allowListed")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="kv">
                  <div className="kv__label">{t("ruleWizard.review.who")}</div>
                  <div className="kv__value">{selectedDeviceGroup?.name ?? "—"}</div>
                  <div className="app-pkg" style={{ marginTop: 2 }}>
                    {formatMessage("ruleWizard.review.deviceCount", {
                      count: String(selectedDeviceGroup?.members_count ?? 0),
                    })}
                  </div>
                </div>
                <div className="kv">
                  <div className="kv__label">{t("ruleWizard.review.activation")}</div>
                  <div className="kv__value">{t(`policyActivation.${activation}`)}</div>
                  {selectedSchedule && (
                    <div className="app-pkg" style={{ marginTop: 2 }}>
                      {selectedSchedule.name}
                    </div>
                  )}
                </div>
                <div className="kv">
                  <div className="kv__label">{t("ruleWizard.review.priority")}</div>
                  <div className="kv__value num">{priority}</div>
                </div>
                <div className="kv">
                  <div className="kv__label">
                    {t("ruleWizard.review.unknownDomain")}
                  </div>
                  <div className="kv__value">
                    {t(`unknownDomainAction.${unknownDomainAction}`)}
                  </div>
                </div>
              </div>

              <div>
                <div className="kv__label" style={{ marginBottom: 8 }}>
                  {t("ruleWizard.review.destinations")}
                </div>
                {selectedUrlGroups.length === 0 ? (
                  <span className="app-pkg">{t("rules.column.whatEmpty")}</span>
                ) : (
                  <ul style={{ paddingLeft: 18, margin: 0 }}>
                    {selectedUrlGroups.map((g) => (
                      <li key={g.id} style={{ fontSize: 13.5 }}>
                        {g.name}
                        <span className="app-pkg" style={{ marginLeft: 8 }}>
                          {formatMessage("ruleWizard.destinations.summarySingular", {
                            count: String(g.members_count),
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <Alert tone="info" title={t("ruleWizard.review.impactTitle")}>
                {formatMessage("ruleWizard.review.impactBody", {
                  count: String(selectedDeviceGroup?.members_count ?? 0),
                })}
              </Alert>

              {conflictingRules.length > 0 && (
                <Alert tone="warning" title={t("ruleWizard.review.conflictsTitle")}>
                  {formatMessage("ruleWizard.review.conflictsBody", {
                    count: String(conflictingRules.length),
                  })}
                  <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                    {conflictingRules.slice(0, 4).map((r) => (
                      <li key={r.id} style={{ fontSize: 12 }}>
                        #{r.priority} · {r.name}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </>
          )}

          <div className="wizard__nav">
            <Button variant="ghost" disabled={stepIdx === 0} onClick={() => go(-1)}>
              {t("ruleWizard.actions.previous")}
            </Button>
            {step !== "review" ? (
              <Button onClick={attemptNext} disabled={!canProceed()}>
                {t("ruleWizard.actions.next")}
              </Button>
            ) : (
              <Button onClick={() => submitMut.mutate()} loading={submitMut.isPending}>
                {t("ruleWizard.actions.submit")}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
